import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./globals.css";
import { browserReloadGuard, importWithReload } from "./common/lib/dynamic-import";
import GenericErrorPage from "./common/pages/generic-error-page";
import { ErrorBoundary, initSentry, reportError } from "./common/services/sentry.service";

const hostname = typeof window !== "undefined" ? window.location.hostname : "";
const isAdminSubdomain = hostname.startsWith("admin.");
const isPartnersSubdomain = hostname.startsWith("partners.");
const forcedMarketing = import.meta.env.VITE_APP_TARGET === "marketing";
const isProductApp = !forcedMarketing && (hostname.startsWith("app.") || import.meta.env.DEV);

const loadApp = (): Promise<React.ComponentType> =>
	isAdminSubdomain
		? import("./admin/admin-app").then((m) => m.default)
		: isPartnersSubdomain
			? import("./partners/partners-app").then((m) => m.default)
			: isProductApp
				? import("./app").then((m) => m.default)
				: import("./marketing/marketing-app").then((m) => m.default);

const resolveTarget = (): string => {
	if (isAdminSubdomain) return "admin";
	if (isPartnersSubdomain) return "partners";
	return isProductApp ? "app" : "marketing";
};

const render = (children: React.ReactNode): void => {
	const root = document.getElementById("root");
	if (!root) {
		return;
	}
	createRoot(root).render(<StrictMode>{children}</StrictMode>);
};

void importWithReload(loadApp, browserReloadGuard)
	.then((AppComponent) => {
		render(
			<ErrorBoundary fallback={<GenericErrorPage />}>
				<AppComponent />
			</ErrorBoundary>,
		);
	})
	.catch((error: unknown) => {
		initSentry();
		reportError(error, { stage: "bootstrap", target: resolveTarget() });
		render(<GenericErrorPage />);
	});

const deferIdle = (cb: () => void): void => {
	if (typeof window === "undefined") return;
	if (typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(() => cb());
		return;
	}
	window.setTimeout(cb, 1500);
};

deferIdle(() => {
	initSentry();
});
